import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Row, Col, Button, Spinner, Card, Modal as BSModal } from "react-bootstrap";
import { Plus } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Formik,
  Form as FormikForm,
  useFormikContext,
} from "formik";
import * as Yup from "yup";

// Import common components
import TextInput from "../../../common/FormFields/TextInput.jsx";
import CustomAsyncDropdown from "../../../common/CustomAsyncDropdown.jsx";

// Import services
import {
  getOrganizations,
  getOrganizationById,
  updateOrganization,
} from "../../../../../services/organizationServices.js";
import { getContacts, updateContact, createContact } from "../../../../../services/contactServices.js";
import { getPlacementOfficers } from "../../../../../services/PlacementOfficerServices.js";
import { getMe } from "../../../../../services/AuthService";

// ─── AddContactForm component ───────────────────────────────────────────────
const AddContactForm = ({ onContactCreated, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [officerPage, setOfficerPage] = useState(1);
  const [hasMoreOfficers, setHasMoreOfficers] = useState(true);
  const [officerSearch, setOfficerSearch] = useState("");
  const [initialValues, setInitialValues] = useState({
    name: "",
    email: "",
    phone: "",
    organizationId: "Not Available",
    assignedToId: "",
  });

  const loadCurrentUser = async () => {
    try {
      const response = await getMe();
      if (response && response.data) {
        setCurrentUser(response.data);
        setInitialValues(prev => ({
          ...prev,
          assignedToId: response.data.id
        }));
      }
    } catch (error) {
      console.error("Failed to load current user:", error);
    }
  };

  const loadUsers = async (isNewSearch = false, page = 1, search = "") => {
    try {
      const response = await getPlacementOfficers(20, page, search);
      if (response && response.data) {
        const mapped = response.data.map((officer) => ({
          value: officer.id,
          label: `${officer.firstName || ""} ${officer.lastName || ""} | ${officer.emailId || officer.email || ""}`.trim(),
        }));
        setUserOptions(prev => page === 1 ? mapped : [...prev, ...mapped]);
        setHasMoreOfficers(response.pagination?.page < response.pagination?.totalPages);
      }
    } catch (error) {
      console.error("Failed to load officers:", error);
    }
  };

  useEffect(() => {
    loadCurrentUser();
    loadUsers(true, 1, "");
  }, []);

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      const payload = {
        ...values,
        createdById: currentUser?.id || "current-user-id",
        updatedById: currentUser?.id || "current-user-id",
        removeFromOrganizationId: "",
      };
      const result = await createContact(payload);
      toast.success(`Contact ${values.name} created successfully!`);
      if (onContactCreated) onContactCreated(result);
    } catch (error) {
      toast.error(error.message || "Failed to create contact");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        phone: Yup.string().required("Phone is required"),
        organizationId: Yup.string().required("Organization is required"),
        assignedToId: Yup.string().required("Assigned To is required"),
      })}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ setFieldValue, values }) => (
        <FormikForm className="p-3">
          <TextInput label="Full Name" name="name" required />
          <TextInput label="Email" name="email" required />
          <TextInput label="Phone" name="phone" required />
          <div className="mb-4">
            <label className="form-label text-dark fw-bold">* Assigned To</label>
            <CustomAsyncDropdown
              isMulti={false}
              options={userOptions}
              value={userOptions.find(o => o.value === values.assignedToId) || null}
              onChange={(val) => setFieldValue("assignedToId", val?.value || "")}
              onInputChange={(s) => {
                setOfficerSearch(s);
                loadUsers(true, 1, s);
              }}
              onMenuScrollToBottom={() => {
                if (hasMoreOfficers) {
                  const nextPage = officerPage + 1;
                  setOfficerPage(nextPage);
                  loadUsers(false, nextPage, officerSearch);
                }
              }}
              placeholder="Select assigned officer"
            />
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="primary" style={{ backgroundColor: "#ea580c", border: "none" }}>Create Contact</Button>
          </div>
        </FormikForm>
      )}
    </Formik>
  );
};

const PAGE_LIMIT = 20;

// ─── ScrollToFieldError ───────────────────────────────────────────────────────
const ScrollToFieldError = () => {
  const { submitCount, isValid, errors } = useFormikContext();
  useEffect(() => {
    if (isValid || submitCount === 0) return;
    const fieldErrorNames = Object.keys(errors);
    if (!fieldErrorNames.length) return;
    setTimeout(() => {
      const el =
        document.querySelector(`input[name='${fieldErrorNames[0]}']`) ||
        document.querySelector(`textarea[name='${fieldErrorNames[0]}']`) ||
        document.querySelector(`[data-field='${fieldErrorNames[0]}']`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  }, [submitCount, isValid, errors]);
  return null;
};

const OrganizationDetailsTab = (props) => {
  const {
    organization,
    onHide,
    onUpdateOrganization,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const isComponentMounted = useRef(true);

  // ── Dropdown state ─────────────────────────────────────────────────────────
  const [officersOptions, setOfficersOptions] = useState([]);
  const [officersPage, setOfficersPage] = useState(1);
  const [officersHasMore, setOfficersHasMore] = useState(true);
  const [officersLoading, setOfficersLoading] = useState(false);
  const [officersSearch, setOfficersSearch] = useState("");

  const [contactsOptions, setContactsOptions] = useState([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsHasMore, setContactsHasMore] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsSearch, setContactsSearch] = useState("");
  const [hasFetchedOfficers, setHasFetchedOfficers] = useState(false);
  const [hasFetchedContacts, setHasFetchedContacts] = useState(false);
  const [showCreateContactModal, setShowCreateContactModal] = useState(false);

  const abortControllers = useRef({
    officers: null,
    contacts: null,
  });

  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
      Object.keys(abortControllers.current).forEach(key => {
        if (abortControllers.current[key]) {
          abortControllers.current[key].abort();
        }
      });
    };
  }, []);

  // ── Fetch Functions ────────────────────────────────────────────────────────
  
  const fetchOfficers = useCallback(async ({ page = 1, search = "", append = false } = {}) => {
    if (officersLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.officers) abortControllers.current.officers.abort();
    const abortController = new AbortController();
    abortControllers.current.officers = abortController;
    
    setOfficersLoading(true);
    try {
      const response = await getPlacementOfficers(PAGE_LIMIT, page, search, { signal: abortController.signal });
      if (!isComponentMounted.current) return;
      
      const mapped = (response?.data || []).map((o) => ({
        value: o.id,
        label: `${o.firstName} ${o.lastName} | ${o.emailId}`,
        officerData: o
      }));
      
      setOfficersOptions((prev) => {
        if (!append) return mapped;
        // Avoid duplicates when appending
        const existingIds = new Set(prev.map(o => o.value));
        return [...prev, ...mapped.filter(o => !existingIds.has(o.value))];
      });
      setOfficersPage(page);
      setOfficersHasMore(page < (response?.pagination?.totalPages || 1));
      setHasFetchedOfficers(true);
    } catch (e) {
      if (e.name !== 'AbortError') console.error("Failed to load officers:", e);
    } finally {
      if (isComponentMounted.current) setOfficersLoading(false);
    }
  }, [officersLoading]);

  const fetchContacts = useCallback(async ({ page = 1, search = "", append = false } = {}) => {
    if (contactsLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.contacts) abortControllers.current.contacts.abort();
    const abortController = new AbortController();
    abortControllers.current.contacts = abortController;
    
    setContactsLoading(true);
    try {
      // Correctly passing search as an object to getContacts
      const response = await getContacts(PAGE_LIMIT, page, { name: search }, { signal: abortController.signal });
      if (!isComponentMounted.current) return;
      
      const mapped = (response?.data || []).map((c) => ({
        value: c.id,
        label: `${c.name} | ${c.email}`,
        contactData: c
      }));
      
      setContactsOptions((prev) => {
        if (!append) return mapped;
        // Avoid duplicates when appending
        const existingIds = new Set(prev.map(o => o.value));
        return [...prev, ...mapped.filter(o => !existingIds.has(o.value))];
      });
      setContactsPage(page);
      setContactsHasMore(page < (response?.pagination?.totalPages || 1));
      setHasFetchedContacts(true);
    } catch (e) {
      if (e.name !== 'AbortError') console.error("Failed to load contacts:", e);
    } finally {
      if (isComponentMounted.current) setContactsLoading(false);
    }
  }, [contactsLoading]);

  const hasInitialLoaded = useRef(false);

  // Sync with parent data if it arrives late
  useEffect(() => {
    if (props.contacts?.length > 0) {
      const mapped = props.contacts.map(c => ({ 
        value: c.id, 
        label: `${c.name} | ${c.email}`,
        contactData: c 
      }));
      setContactsOptions(mapped);
    }
  }, [props.contacts]);

  useEffect(() => {
    if (props.users?.length > 0) {
      const mapped = props.users.map(u => ({ 
        value: u.id, 
        label: `${u.firstName} ${u.lastName} | ${u.emailId}`,
        officerData: u 
      }));
      setOfficersOptions(mapped);
    }
  }, [props.users]);

  // Initial Load
  useEffect(() => {
    if (!organization?.id || hasInitialLoaded.current) return;
    
    const initializeData = async () => {
      hasInitialLoaded.current = true;
      // Pre-population is already handled by the sync hooks above
    };

    initializeData();
  }, [organization?.id]);

  // ── Fetch Full Data if missing ──────────────────────────────────────────
  const [fullOrganization, setFullOrganization] = useState(organization);

  // Update fullOrganization if prop changes
  useEffect(() => {
    setFullOrganization(organization);
  }, [organization]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  
  const parseIds = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) {
      return field.map(item => typeof item === 'object' ? item.id : String(item)).filter(Boolean);
    }
    if (typeof field === 'string') {
      return field.split(',').map(s => s.trim()).filter(Boolean);
    }
    if (typeof field === 'object' && field !== null) {
      return field.id ? [field.id] : [];
    }
    return [];
  };

  const resolveSingleId = (field) => {
    const ids = parseIds(field);
    return ids.length > 0 ? ids[0] : "";
  };

  const getInitialValues = () => {
    const data = fullOrganization || organization;
    if (!data) return {
      name: "", website: "", industry: "", organizationType: "",
      contactPersonIds: [], assignedToIds: "", status: "",
      address: "", city: "", state: "", PinCode: "", country: "",
      description: "", employeeCount: "", annualRevenue: "",
      socialMediaLinkedin: "", socialMediaTwitter: "", socialMediaFacebook: "",
      notes: ""
    };
    
    return {
      name: data.name || "",
      website: data.website || "",
      industry: data.industry || "",
      organizationType: data.organizationType || "",
      contactPersonIds: parseIds(data.contactPersonIds),
      assignedToIds: resolveSingleId(data.assignedToIds),
      status: data.status || "",
      address: data.address || "",
      city: data.city || "",
      state: data.state || "",
      PinCode: data.PinCode || "",
      country: data.country || "",
      description: data.description || "",
      employeeCount: data.employeeCount || "",
      annualRevenue: data.annualRevenue || "",
      socialMediaLinkedin: data.socialMediaLinkedin || "",
      socialMediaTwitter: data.socialMediaTwitter || "",
      socialMediaFacebook: data.socialMediaFacebook || "",
      notes: data.notes || ""
    };
  };

  const validationSchema = Yup.object({
    name: Yup.string().min(2).max(200).required("Organization name is required"),
    website: Yup.string().url("Invalid website URL").nullable(),
    industry: Yup.string().required("Industry is required"),
    organizationType: Yup.string().required("Organization type is required"),
    contactPersonIds: Yup.array().min(1, "At least one contact person is required"),
    assignedToIds: Yup.string().required("Assigned To is required"),
    status: Yup.string().required("Status is required"),
    address: Yup.string().required("Address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    PinCode: Yup.string().matches(/^\d{6}$/, "Must be a 6-digit pin code").required("Pin code is required"),
    country: Yup.string().required("Country is required"),
  });

  const hardcodedOptions = {
    industries: [
      { value: "technology", label: "Information & Technology" },
      { value: "finance", label: "Finance" },
      { value: "education", label: "Education" },
      { value: "consulting", label: "Consulting" },
      { value: "other", label: "Other" },
    ],
    organizationTypes: [
      { value: "corporation", label: "Corporation" },
      { value: "llc", label: "LLC (Limited Liability Company)" },
      { value: "startup", label: "Startup" },
      { value: "private-company", label: "Private Company" },
      { value: "other", label: "Other" },
    ],
    status: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "partner", label: "Partner" },
      { value: "competitor", label: "Competitor" },
      { value: "other", label: "Other" },
    ],
  };

  const renderContactOption = (option) => {
    const org = Array.isArray(option.contactData?.organizationId)
      ? option.contactData.organizationId[0]?.name
      : typeof option.contactData?.organizationId === "string"
      ? option.contactData.organizationId
      : "Not Available";

    return (
      <div>
        <div style={{ fontWeight: "bold", fontSize: "0.95em" }}>
          {option.contactData?.name || option.label?.split(' | ')[0]}
        </div>
        <div style={{ fontSize: "0.85em", color: "#666", marginTop: "2px" }}>
          {option.contactData?.email || option.label?.split(' | ')[1]} | {org}
        </div>
      </div>
    );
  };

  const menuHeader = (
    <div style={{ padding: "8px" }}>
      <Button
        variant="outline-primary"
        size="sm"
        className="w-100 d-flex align-items-center justify-content-center"
        onClick={() => setShowCreateContactModal(true)}
        style={{
          borderColor: "#ea580c",
          color: "#ea580c",
          fontSize: "0.85em",
          padding: "6px 12px",
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = "#ea580c";
          e.target.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = "transparent";
          e.target.style.color = "#ea580c";
        }}
      >
        <Plus size={14} className="me-2" />
        Create New Contact
      </Button>
    </div>
  );

  // ── Sync Logic ─────────────────────────────────────────────────────────────
  


  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setIsLoading(true);
      const originalContactIds = parseIds(organization.contactPersonIds);
      
      const currentContactIds = Array.isArray(values.contactPersonIds)
        ? values.contactPersonIds.map(id => typeof id === 'object' ? id.id : id)
        : (typeof values.contactPersonIds === 'object' && values.contactPersonIds !== null)
          ? [values.contactPersonIds.id]
          : values.contactPersonIds ? values.contactPersonIds.split(",").map(s => s.trim()) : [];

      const removeFromContacts = originalContactIds.filter(id => !currentContactIds.includes(id));
      
      const payload = {
        name: values.name,
        website: values.website,
        industry: values.industry,
        organizationType: values.organizationType,
        contactPersonIds: currentContactIds.join(", "),
        removeFromContacts: removeFromContacts,
        assignedToIds: Array.isArray(values.assignedToIds)
          ? (values.assignedToIds[0]?.id || values.assignedToIds[0] || "")
          : (typeof values.assignedToIds === 'object' && values.assignedToIds !== null)
            ? values.assignedToIds.id
            : values.assignedToIds || "",
        status: values.status,
        address: values.address,
        city: values.city,
        state: values.state,
        PinCode: values.PinCode,
        country: values.country,
        description: values.description,
        employeeCount: values.employeeCount,
        annualRevenue: values.annualRevenue,
        socialMediaLinkedin: values.socialMediaLinkedin,
        socialMediaTwitter: values.socialMediaTwitter,
        socialMediaFacebook: values.socialMediaFacebook,
        notes: values.notes,
        updatedAt: new Date().toISOString(),
        updatedById: "current-user-id", // Should be from auth context
      };

      await updateOrganization(organization.id, payload);



      toast.success("Organization updated successfully!");
      
      // Notify parent to refresh - this handles the list update
      if (onUpdateOrganization) {
        onUpdateOrganization(organization.id, payload);
      } else if (window.refreshOrganizationsData) {
        window.refreshOrganizationsData();
      }
      
      if (window.refreshContactsData) window.refreshContactsData();
      if (onHide) onHide();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.message || "Failed to update organization");
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  if (!organization) return null;

  return (
    <div className="p-4">
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, isSubmitting: formikSubmitting, dirty }) => (
          <FormikForm>
            <ScrollToFieldError />
            
            {/* Basic Details Section */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header style={{ backgroundColor: "#fff7ed" }}>
                <h6 className="mb-0 text-dark fw-bold">Basic Details</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-4">
                      <label className="form-label text-dark fw-bold">* HR Contacts</label>
                      <CustomAsyncDropdown
                        isMulti={true}
                        options={contactsOptions}
                        value={contactsOptions.filter(o => values.contactPersonIds.includes(o.value)).length > 0
                          ? contactsOptions.filter(o => values.contactPersonIds.includes(o.value))
                          : (Array.isArray(organization.contactPersonIds) 
                            ? organization.contactPersonIds.filter(c => values.contactPersonIds.includes(c.id || c)).map(c => ({
                                value: c.id || c,
                                label: `${c.name || "Unknown"} | ${c.email || ""}`,
                                contactData: c
                              }))
                            : [])}
                        onChange={(val) => setFieldValue("contactPersonIds", val ? val.map(o => o.value) : [])}
                        onInputChange={(s) => {
                          setContactsSearch(s);
                          fetchContacts({ page: 1, search: s, append: false });
                        }}
                        onMenuOpen={() => fetchContacts({ page: 1, search: contactsSearch, append: false })}
                        onMenuScrollToBottom={() => {
                          if (contactsHasMore && !contactsLoading) {
                            fetchContacts({ page: contactsPage + 1, search: contactsSearch, append: true });
                          }
                        }}
                        onRefresh={() => fetchContacts({ page: 1, search: "", append: false })}
                        isLoading={contactsLoading}
                        placeholder="Search and select contacts"
                        renderOption={renderContactOption}
                        menuHeader={menuHeader}
                      />
                      {touched.contactPersonIds && errors.contactPersonIds && (
                        <div className="text-danger small mt-1">{errors.contactPersonIds}</div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <TextInput label="Organization Name" name="name" required />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <TextInput label="Website" name="website" placeholder="https://example.com" />
                  </Col>
                  <Col md={6}>
                    <div className="mb-4">
                      <label className="form-label text-dark fw-bold">* Industry</label>
                      <CustomAsyncDropdown
                        isMulti={false}
                        options={hardcodedOptions.industries}
                        value={hardcodedOptions.industries.find(o => o.value.toLowerCase() === (values.industry || "").toLowerCase()) || (values.industry ? { value: values.industry, label: values.industry } : null)}
                        onChange={(val) => setFieldValue("industry", val?.value || "")}
                        placeholder="Select Industry"
                      />
                      {touched.industry && errors.industry && (
                        <div className="text-danger small mt-1">{errors.industry}</div>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-4">
                      <label className="form-label text-dark fw-bold">* Organization Type</label>
                      <CustomAsyncDropdown
                        isMulti={false}
                        options={hardcodedOptions.organizationTypes}
                        value={hardcodedOptions.organizationTypes.find(o => o.value.toLowerCase() === (values.organizationType || "").toLowerCase()) || (values.organizationType ? { value: values.organizationType, label: values.organizationType } : null)}
                        onChange={(val) => setFieldValue("organizationType", val?.value || "")}
                        placeholder="Select Organization Type"
                      />
                      {touched.organizationType && errors.organizationType && (
                        <div className="text-danger small mt-1">{errors.organizationType}</div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-4">
                      <label className="form-label text-dark fw-bold">* Status</label>
                      <CustomAsyncDropdown
                        isMulti={false}
                        options={hardcodedOptions.status}
                        value={hardcodedOptions.status.find(o => o.value.toLowerCase() === (values.status || "").toLowerCase()) || (values.status ? { value: values.status, label: values.status } : null)}
                        onChange={(val) => setFieldValue("status", val?.value || "")}
                        placeholder="Select Status"
                      />
                      {touched.status && errors.status && (
                        <div className="text-danger small mt-1">{errors.status}</div>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-4">
                      <label className="form-label text-dark fw-bold">* Assigned To</label>
                      <CustomAsyncDropdown
                        isMulti={false}
                        options={officersOptions}
                        value={officersOptions.find(o => o.value === values.assignedToIds) || 
                          (Array.isArray(organization.assignedToIds) && organization.assignedToIds.length > 0
                            ? { 
                                value: organization.assignedToIds[0].id || organization.assignedToIds[0], 
                                label: `${organization.assignedToIds[0].firstName || ""} ${organization.assignedToIds[0].lastName || ""}`.trim() || organization.assignedToIds[0].name || "Unknown",
                                officerData: organization.assignedToIds[0]
                              }
                            : (organization.assignedToIds && typeof organization.assignedToIds === 'object'
                                ? {
                                    value: organization.assignedToIds.id,
                                    label: `${organization.assignedToIds.firstName || ""} ${organization.assignedToIds.lastName || ""}`.trim() || organization.assignedToIds.name || "Unknown",
                                    officerData: organization.assignedToIds
                                  }
                                : null))}
                        onChange={(val) => setFieldValue("assignedToIds", val?.value || "")}
                        onInputChange={(s) => {
                          setOfficersSearch(s);
                          fetchOfficers({ page: 1, search: s, append: false });
                        }}
                        onMenuOpen={() => fetchOfficers({ page: 1, search: officersSearch, append: false })}
                        onMenuScrollToBottom={() => {
                          if (officersHasMore && !officersLoading) {
                            fetchOfficers({ page: officersPage + 1, search: officersSearch, append: true });
                          }
                        }}
                        onRefresh={() => fetchOfficers({ page: 1, search: "", append: false })}
                        isLoading={officersLoading}
                        placeholder="Search and select officer"
                      />
                      {touched.assignedToIds && errors.assignedToIds && (
                        <div className="text-danger small mt-1">{errors.assignedToIds}</div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Contact Details Section */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header style={{ backgroundColor: "#fff7ed" }}>
                <h6 className="mb-0 text-dark fw-bold">Additional Information</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}><TextInput label="Employee Count" name="employeeCount" /></Col>
                  <Col md={6}><TextInput label="Annual Revenue" name="annualRevenue" /></Col>
                </Row>
                <Row>
                  <Col md={6}><TextInput label="LinkedIn URL" name="socialMediaLinkedin" /></Col>
                  <Col md={6}><TextInput label="Twitter URL" name="socialMediaTwitter" /></Col>
                </Row>
                <Row>
                  <Col md={6}><TextInput label="Facebook URL" name="socialMediaFacebook" /></Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <TextInput label="Description" name="description" as="textarea" rows={3} />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <TextInput label="Notes" name="notes" as="textarea" rows={3} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Address Details Section */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header style={{ backgroundColor: "#fff7ed" }}>
                <h6 className="mb-0 text-dark fw-bold">Address Details</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}><TextInput label="Address" name="address" required /></Col>
                </Row>
                <Row>
                  <Col md={6}><TextInput label="City" name="city" required /></Col>
                  <Col md={6}><TextInput label="State" name="state" required /></Col>
                </Row>
                <Row>
                  <Col md={6}><TextInput label="Pin Code" name="PinCode" required maxLength={6} /></Col>
                  <Col md={6}><TextInput label="Country" name="country" required /></Col>
                </Row>
              </Card.Body>
            </Card>

            <Row className="mt-4">
              <Col md={12} className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={onHide} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!dirty || isLoading}
                  style={{ backgroundColor: "#ea580c", borderColor: "#ea580c" }}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : "Update Organization"}
                </Button>
              </Col>
            </Row>

            {/* Create Contact Modal */}
            <BSModal 
              show={showCreateContactModal} 
              onHide={() => setShowCreateContactModal(false)}
              size="lg"
              centered
              backdrop="static"
            >
              <BSModal.Header closeButton className="border-0">
                <BSModal.Title>Create New Contact</BSModal.Title>
              </BSModal.Header>
              <BSModal.Body>
                <AddContactForm 
                  onCancel={() => setShowCreateContactModal(false)}
                  onContactCreated={(newContact) => {
                    const contactData = newContact?.data || newContact;
                    const newOption = {
                      value: contactData.id,
                      label: `${contactData.name} | ${contactData.email}`,
                      contactData: contactData
                    };
                    
                    // Add to options
                    setContactsOptions(prev => [newOption, ...prev]);
                    
                    // Select it in formik
                    const currentIds = values.contactPersonIds || [];
                    if (!currentIds.includes(contactData.id)) {
                      setFieldValue("contactPersonIds", [...currentIds, contactData.id]);
                    }
                    
                    setShowCreateContactModal(false);
                  }}
                />
              </BSModal.Body>
            </BSModal>
          </FormikForm>
        )}
      </Formik>
      <ToastContainer />
    </div>
  );
};

export default OrganizationDetailsTab;
